import { LinearProgress, linearProgressClasses, styled } from '@mui/material';
import { cn } from '@tempo-io/sui-class-utils';
import type { ReactNode } from 'react';
import { useSwimlaneRootProps } from '@/components/ui/swimlane/context';
import { bindCardClickDisabledHandler } from '@/components/ui/swimlane/layout/hooks/useCardEventHandlers';
import type { ICard } from '@/components/ui/swimlane/types';
import { getHasProgress } from '../CardUtils';

type TProgressProps = {
  progress: ICard['progress'];
  isActionsExpanded: boolean;
  barColor: string;
  getSubItemsButton: (canHaveLabel: boolean) => ReactNode | null;
};

export const Progress = ({
  progress,
  isActionsExpanded,
  barColor,
  getSubItemsButton,
}: TProgressProps) => {
  const { isCompact, isSubItemFeaturesEnabled } = useSwimlaneRootProps();

  if (progress === null) {
    return null;
  }

  const hasProgress = getHasProgress({ progress });
  const subItemsButton = isSubItemFeaturesEnabled && isCompact && getSubItemsButton(!hasProgress);
  if (!hasProgress && !subItemsButton) {
    return null;
  }
  // Already not null here, this makes typescript happy

  const percentage = progress * 100;

  return (
    <div
      data-testid="progress-bar"
      {...bindCardClickDisabledHandler()}
      className={cn('flex h-4 items-center justify-center pt-0! w-full', {
        'px-1': isCompact,
        'px-2': !isCompact,
        'my-0.5': isCompact,
        'my-1': isCompact && isActionsExpanded,
        'justify-end': !hasProgress && Boolean(subItemsButton),
      })}
    >
      {hasProgress && (
        <>
          <StyledLinearProgressBar
            $barColor={barColor}
            variant="determinate"
            value={percentage as number}
          />
          <span className="ml-1 text-xs leading-3">{percentage}%</span>
        </>
      )}
      {Boolean(subItemsButton) && (
        <span className="text-xs leading-3 ml-auto">{subItemsButton}</span>
      )}
    </div>
  );
};

const StyledLinearProgressBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== '$barColor',
})<{ $barColor: string }>`
  flex: 1;
  height: 6px;
  border-radius: 9999px;
  &.${linearProgressClasses.determinate} {
    background-color: #E8E8E8;
  },
  &.${linearProgressClasses.determinate} > .${linearProgressClasses.bar1Determinate} {
    background-color: ${({ $barColor }) => $barColor};
  },
`;
